import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TOPICS } from '../../graphql';
import { Link, Video, Image, Upload, Search } from 'lucide-react';
import { TopicCard } from './TopicCard';
import { Topic, MediaType, Account } from '../../types';
import { GetTopicsResponse } from '../../types/graphql';
import { CreateTopicModal } from './CreateTopicModal';
import { TopicDetailsModal } from './TopicDetailsModal';
import { Loader } from '../common/PageLoader';

interface TopicsSectionProps {
  accountId?: string;
  onTopicSelect: (topic: Topic) => void;
  mediaFilters: MediaType[];
  onMediaFilterChange: (event: any, newFilters: MediaType[]) => void;
  selectedAccount?: Pick<Account, 'id'> | null;
  selectedPost?: any;
  onRefetchReady?: (refetch: () => Promise<any>) => void;
  clickBehavior?: 'select' | 'details'; // 'select' calls onTopicSelect, 'details' shows details modal
}

export const TopicsSection = React.memo<TopicsSectionProps>(
  ({
    accountId,
    onTopicSelect,
    mediaFilters,
    onMediaFilterChange,
    selectedAccount,
    onRefetchReady,
    clickBehavior = 'select',
  }) => {
    const [isTopicModalOpen, setIsTopicModalOpen] = useState<boolean>(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
    const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const {
      loading,
      error,
      data: topicsData,
      refetch,
    } = useQuery<GetTopicsResponse>(GET_TOPICS, {
      variables: { accountId: selectedAccount?.id || '' },
      skip: !selectedAccount?.id,
      fetchPolicy: 'cache-and-network',
    });

    // Expose refetch function to parent - only if accountId exists
    useEffect(() => {
      if (!onRefetchReady) return;

      // Only set refetch function if we have a valid accountId and refetch is available
      if (selectedAccount?.id && refetch) {
        onRefetchReady(() => {
          // Double-check accountId still exists before refetching
          if (selectedAccount?.id) {
            return refetch();
          }
          return Promise.resolve();
        });
      } else {
        // Set a no-op function if accountId is missing or refetch is not available
        onRefetchReady(async () => {
          // No-op: no accountId or query is skipped, nothing to refetch
          return Promise.resolve();
        });
      }
    }, [onRefetchReady, refetch, selectedAccount?.id]);

    const handleTopicModalClose = () => {
      setIsTopicModalOpen(false);
    };

    const handleTopicCreated = () => {
      refetch();
    };

    const handleTopicClick = (topic: Topic) => {
      if (clickBehavior === 'details') {
        setSelectedTopic(topic);
        setIsDetailsModalOpen(true);
      } else {
        onTopicSelect(topic);
      }
    };

    const handleCloseDetailsModal = () => {
      setIsDetailsModalOpen(false);
      setSelectedTopic(null);
    };

    // Determine active filter for UI display
    // Check if all media types are selected (ALL)
    const allMediaTypes = ['IMAGE', 'VIDEO', 'GALLERY', 'PHOTO', 'BOOKMARK'];
    const isAllSelected = allMediaTypes.every((type) => mediaFilters.includes(type as MediaType));
    // Check if Bookmarks filter is active
    // Case 1: Both IMAGE and BOOKMARK selected (and only those two)
    // Case 2: Only BOOKMARK selected
    const isBookmarksFilter =
      (mediaFilters.length === 2 &&
        mediaFilters.includes('IMAGE' as MediaType) &&
        mediaFilters.includes('BOOKMARK' as MediaType)) ||
      (mediaFilters.length === 1 && mediaFilters.includes('BOOKMARK' as MediaType));
    // Check if Photos filter is active (IMAGE, PHOTO, and/or GALLERY selected, and no other types)
    const photoTypes = ['IMAGE', 'PHOTO', 'GALLERY'];
    const isPhotosFilter =
      mediaFilters.length > 0 &&
      mediaFilters.every((filter) => photoTypes.includes(filter as string)) &&
      !mediaFilters.includes('VIDEO' as MediaType) &&
      !mediaFilters.includes('BOOKMARK' as MediaType);
    const activeFilter = isAllSelected
      ? 'ALL'
      : isBookmarksFilter
        ? 'BOOKMARK'
        : isPhotosFilter
          ? 'PHOTO'
          : mediaFilters[0] || 'ALL';

    const filteredTopics =
      topicsData?.topics
        ?.filter((topic) => {
          // Filter by media type - check if topic's mediaType is in the mediaFilters array
          if (activeFilter === 'ALL') {
            // Show all topics
          } else if (isBookmarksFilter) {
            // Bookmarks filter: show both BOOKMARK and IMAGE
            if (topic.mediaType !== 'BOOKMARK' && topic.mediaType !== 'IMAGE') {
              return false;
            }
          } else {
            // Check if the topic's mediaType is included in the mediaFilters array
            if (!mediaFilters.includes(topic.mediaType as MediaType)) {
              return false;
            }
          }

          // Filter by search query (title, summary, and keywords)
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            const titleMatch = topic.title?.toLowerCase().includes(query) || false;
            const summaryMatch = topic.summary?.toLowerCase().includes(query) || false;
            const categoryMatch = topic.category?.toLowerCase().includes(query) || false;

            // Check if topic has keywords field (it might not be in the query yet)
            const keywords = (topic as any).keywords;
            const keywordsMatch =
              //eslint-disable-next-line
              (Array.isArray(keywords) &&
                keywords.some(
                  (keyword: string) => keyword.toLowerCase().includes(query)
                  //eslint-disable-next-line
                )) ||
              false;

            if (!titleMatch && !summaryMatch && !keywordsMatch && !categoryMatch) {
              return false;
            }
          }

          return true;
        })
        .sort(
          (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
        ) || [];

    return (
      <div className="bg-core-dark p-6 h-full flex flex-col">
        <div className="mb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold text-cmp-ink">Media Library</h2>
        </div>

        {/* Add Topic Modal */}
        {accountId && (
          <CreateTopicModal
            isOpen={isTopicModalOpen}
            onClose={handleTopicModalClose}
            accountId={accountId}
            onSuccess={handleTopicCreated}
          />
        )}

        {/* Topic Details Modal */}
        <TopicDetailsModal
          topic={selectedTopic}
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
        />

        <div className="mb-4 flex-shrink-0 space-y-3">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cmp-ink-2" />
              <input
                type="text"
                placeholder="Search by title, summary, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-1 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-500 text-sm"
              />
            </div>
            <button
              onClick={() => setIsTopicModalOpen(true)}
              className="px-3 py-1 rounded bg-primary text-onfill hover:bg-primary-dark transition-colors border border-primary flex-shrink-0 flex items-center gap-2"
              title="Upload new content"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">Upload</span>
            </button>
          </div>
          <div className="flex w-full border border-cmp-line-2 rounded overflow-hidden bg-form-dark p-0.5">
            <button
              onClick={() => {
                onMediaFilterChange(null, ['IMAGE', 'VIDEO', 'GALLERY', 'PHOTO', 'BOOKMARK']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'ALL'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <span>All</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['IMAGE', 'BOOKMARK']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                isBookmarksFilter
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Link className="w-3 h-3" />
              <span>Bookmarks</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['VIDEO']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'VIDEO'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Video className="w-3 h-3" />
              <span>Videos</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['PHOTO' as MediaType]);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'PHOTO'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Image className="w-3 h-3" />
              <span>Photos</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center">
            <Loader label="Loading topics" className="text-cmp-ink" />
          </div>
        ) : error ? (
          <div className="p-4 bg-error/20 border border-error rounded text-error">
            Error loading topics: {error.message}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto -mx-6 px-6">
            <div className="grid grid-cols-2 gap-4 pb-4">
              {filteredTopics.map((topic) => (
                <TopicCard key={topic.id} topic={topic} onClick={() => handleTopicClick(topic)} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);
